
import { useState, useEffect } from "react";
import { isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";
import { CRMData, Filters } from "../types";

export const useFilterLogic = (crmData: CRMData[]) => {
  const [filteredData, setFilteredData] = useState<CRMData[]>([]);
  const [filters, setFilters] = useState<Filters>({
    membershipTypes: ["active", "benefactor", "donor"],
    showSignatories: true,
    showNewsletter: true,
    showDonors: true,
    searchTerm: "",
    startDate: undefined,
    endDate: undefined,
    dateField: "all_dates"  // Changed from "date_adhesion" to "all_dates"
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    applyFilters();
  }, [crmData, filters]);

  const applyFilters = () => {
    let result = [...crmData];
    
    console.log("Applying filters:", filters);
    console.log("Initial data count:", result.length);
    
    // Vérifier si aucun filtre de type n'est actif
    const noTypeFilters = filters.membershipTypes.length === 0 && 
                          !filters.showSignatories && 
                          !filters.showNewsletter && 
                          !filters.showDonors;
    
    // Si aucun filtre de type n'est actif, on n'affiche aucun résultat
    if (noTypeFilters) {
      console.log("No type filters active, returning empty result set");
      setFilteredData([]);
      return;
    }
    
    // Nouvelle logique: On garde un enregistrement s'il correspond à AU MOINS UN des filtres activés
    // On commence avec un ensemble vide
    let filteredRecords = new Set<string>();
    
    // Filtrer par type d'adhésion (actif, bienfaiteur, donateur)
    if (filters.membershipTypes.length > 0) {
      const membershipTypeRecords = result.filter(item => 
        item.type_adhesion && filters.membershipTypes.includes(item.type_adhesion)
      );
      
      membershipTypeRecords.forEach(record => {
        filteredRecords.add(record.id);
      });
      
      console.log("Records after membership type filter:", membershipTypeRecords.length);
    }
    
    // Filtrer les signataires non membres (signataire mais pas de type "active" ou "benefactor")
    if (filters.showSignatories) {
      const signatoryRecords = result.filter(item => 
        item.signataire && 
        (!item.type_adhesion || (item.type_adhesion !== 'active' && item.type_adhesion !== 'benefactor'))
      );
      
      signatoryRecords.forEach(record => {
        filteredRecords.add(record.id);
      });
      
      console.log("Records after including non-member signatories:", signatoryRecords.length);
    }
    
    // Filtrer les abonnés newsletter
    if (filters.showNewsletter) {
      const newsletterRecords = result.filter(item => item.abonne_newsletter);
      
      newsletterRecords.forEach(record => {
        filteredRecords.add(record.id);
      });
      
      console.log("Records after including newsletter subscribers:", newsletterRecords.length);
    }
    
    // Filtrer les donateurs additionnels (seulement les adhérents actifs ou bienfaiteurs avec montant_don > 0)
    if (filters.showDonors) {
      const donorRecords = result.filter(item => 
        (item.type_adhesion === 'active' || item.type_adhesion === 'benefactor') && 
        item.montant_don && 
        item.montant_don > 0
      );
      
      donorRecords.forEach(record => {
        filteredRecords.add(record.id);
      });
      
      console.log("Records after including additional donors:", donorRecords.length);
    }
    
    // On ne garde que les enregistrements qui correspondent à au moins un filtre
    result = result.filter(item => filteredRecords.has(item.id));
    
    // Appliquer le filtre de recherche
    if (filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.email && item.email.toLowerCase().includes(searchTerm)) ||
        (item.nom && item.nom.toLowerCase().includes(searchTerm)) ||
        (item.prenom && item.prenom.toLowerCase().includes(searchTerm)) ||
        (item.ville && item.ville.toLowerCase().includes(searchTerm))
      );
      console.log("Records after search filter:", result.length);
    }
    
    // Appliquer le filtre de date
    if (filters.startDate || filters.endDate) {
      result = result.filter(item => {
        // Si le filtre est sur "toutes dates", on vérifie toutes les dates possibles
        if (filters.dateField === "all_dates") {
          const dateFields = ["date_adhesion", "date_signature", "date_abonnement_newsletter"];
          
          // On vérifie si au moins une des dates est dans l'intervalle
          return dateFields.some(field => {
            const dateValue = item[field as keyof CRMData] as string | undefined;
            if (!dateValue) return false;
            
            const itemDate = parseISO(dateValue);
            
            if (filters.startDate && filters.endDate) {
              return isWithinInterval(itemDate, {
                start: startOfDay(filters.startDate),
                end: endOfDay(filters.endDate)
              });
            } else if (filters.startDate) {
              return itemDate >= startOfDay(filters.startDate);
            } else if (filters.endDate) {
              return itemDate <= endOfDay(filters.endDate);
            }
            
            return false;
          });
        } else {
          // Logique existante pour un champ de date spécifique
          const dateField = filters.dateField as keyof CRMData;
          const dateValue = item[dateField] as string | undefined;
          
          if (!dateValue) return false;
          
          const itemDate = parseISO(dateValue);
          
          if (filters.startDate && filters.endDate) {
            return isWithinInterval(itemDate, {
              start: startOfDay(filters.startDate),
              end: endOfDay(filters.endDate)
            });
          } else if (filters.startDate) {
            return itemDate >= startOfDay(filters.startDate);
          } else if (filters.endDate) {
            return itemDate <= endOfDay(filters.endDate);
          }
        }
        
        return true;
      });
      
      console.log("Records after date filter:", result.length);
    }
    
    console.log("Final filtered count:", result.length);
    
    setFilteredData(result);
  };

  return {
    filteredData,
    filters,
    showFilters,
    setFilters,
    setShowFilters,
  };
};
